import { memo, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RootState, useAppSelector } from "../../store/store";
import { FormControl, Grid, InputLabel, ListSubheader, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";

interface OptimizationModelSelectionProps {
    optimizationDataId: string | null
}

const OptimizationModelSelection = ({ optimizationDataId } : OptimizationModelSelectionProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const { optimizationModels } = useAppSelector((state: RootState) => state.optimization);
    
    const groupedModels = useMemo(() => {
        return optimizationModels.reduce((acc: any, model: any) => {
            const { trainingDatasetId, trainingDatasetName } = model;
            acc[trainingDatasetId] = acc[trainingDatasetId] || {
                trainingDatasetName,
                models: [],
            };
            acc[trainingDatasetId].models.push(model);
            return acc;
        }, {});
    }, [optimizationModels]);
    
    const handleModelChange = useCallback(
        (event: SelectChangeEvent) => {
            const modelId = event.target.value;
            const modelName = optimizationModels.find((model: any) => model.id === modelId)?.name;
            if (modelId && modelName) {
                navigate(`?id=${modelId}&name=${modelName}`);
            }
        },
        [optimizationModels, navigate]
    );
    
    return(
        <Grid item pb={1}>
            <FormControl sx={{ mb: 1, mt: 1, minWidth: 300 }} size="small">
                <InputLabel id="demo-select-small-label">
                    {t("OPTIMIZATION_MODELS_SELECT_LABEL")}
                </InputLabel>
                <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={optimizationDataId ?? ""}
                    label={t("OPTIMIZATION_MODELS_SELECT_LABEL")}
                    onChange={handleModelChange}
                >
                    {Object.keys(groupedModels).map((trainingDatasetId, index) => {
                        const group = groupedModels[trainingDatasetId];
                        return [
                            <ListSubheader key={index} className='button-text-color' sx={{ fontWeight: 'bold' }}>
                                {group.trainingDatasetName}
                            </ListSubheader>,
                            ...group.models.map((model: any) => (
                                <MenuItem key={model.id} value={model.id} sx={{ pl: 3.5 }}>
                                    {model.name}{" "}
                                    {model.isShared && (
                                    <Tooltip title={t('OPTIMIZATION_SHARED_MODEL') + model.sharedBy}>
                                        <ShareIcon 
                                            className="info-icon"
                                            sx={{
                                                ml: 1,
                                                fontSize: 18,
                                                cursor: "pointer"
                                            }} 
                                        />
                                    </Tooltip>
                                )}
                                </MenuItem>
                            )),
                        ];
                    })}
                </Select>
            </FormControl>
        </Grid>
    )
}

export default memo(OptimizationModelSelection);